import { Entity } from './common';

// ─── Entry types ─────────────────────────────────────────────────────────────

export type AccountingEntryType = 'INCOME' | 'EXPENDITURE';

// ─── VAT regime (Régimen mixto federaciones) ─────────────────────────────────

/**
 * - `exempt`     : Exenta Art. 20.1 LIVA (cuotas, licencias, inscripciones)
 * - `subject`    : Sujeta y no exenta (patrocinios, venta material)
 * - `not_subject`: No sujeta (subvenciones, donaciones)
 */
export type VatType = 'exempt' | 'subject' | 'not_subject';

export interface VatInfo {
  type: VatType;
  /** IVA rate in % (0, 4, 10, 21). Only when type = 'subject'. */
  rate?: number;
  /** IVA amount in euros. Only when type = 'subject'. */
  amount?: number;
  /** Legal article when exempt, e.g. "Art. 20.1.12 LIVA" */
  exemptionArticle?: string;
}

// ─── Cost Center (Centro de Coste / Proyecto analítico) ──────────────────────
/**
 * Analytic dimension. Every accounting entry MUST be tagged with a cost center.
 * Required by ESFL adapted PGC for sports federations.
 * Stored in `tenants/{tenantId}/accounting_cost_centers/{id}`.
 */
export interface CostCenter extends Entity {
  name: string;
  code: string; // short identifier, e.g. 'CAMP-2026', 'SEL-AUT'
  description?: string;
  fiscalYear: string;
  active: boolean;
  /** Parent cost center ID for hierarchical grouping */
  parentId?: string;
}

// ─── Grant / Subvención ──────────────────────────────────────────────────────
/**
 * Public or private grant received by the federation.
 * Expenses can be linked to a grant with an imputation percentage for
 * justification reports (justificación de subvenciones).
 * Stored in `tenants/{tenantId}/accounting_grants/{id}`.
 */
export type GrantStatus = 'REQUESTED' | 'GRANTED' | 'JUSTIFIED' | 'CLOSED';

export interface Grant extends Entity {
  name: string;
  grantor: string; // Organismo concedente (CSD, Generalitat Valenciana...)
  reference: string; // Número de expediente
  amountGranted: number;
  amountJustified: number; // computed or manually entered
  fiscalYear: string;
  status: GrantStatus;
  description?: string;
  startDate?: Date;
  endDate?: Date;
}

export const GRANT_STATUS_LABELS: Record<GrantStatus, string> = {
  REQUESTED: 'Solicitada',
  GRANTED: 'Concedida',
  JUSTIFIED: 'Justificada',
  CLOSED: 'Cerrada',
};

// ─── AccountingEntry (enhanced for ESFL federations) ─────────────────────────
/**
 * A single entry in the official accounting book.
 * Stored in `tenants/{tenantId}/accounting/{entryId}`.
 *
 * ESFL obligations:
 * - Every entry MUST have a costCenterId (analytic accounting).
 * - VAT regime must be declared (most federation income is exempt Art. 20.1).
 * - Expenses can be partially imputed to a grant.
 */
export interface AccountingEntry extends Entity {
  type: AccountingEntryType;
  concept: string;
  /** Base amount in euros (VAT excluded) */
  amount: number;
  /** VAT info — required for all entries */
  vat: VatInfo;
  /** Total amount = amount + vat.amount (for subject entries) */
  totalAmount: number;
  date: Date;
  fiscalYear: string;
  /**
   * Season this entry belongs to, in "YYYY-YYYY" format (e.g. "2026-2027").
   * Captured from the global season selector when the entry is created.
   * Used by season-scoped views (e.g. club balance). Optional for legacy
   * entries created before this field existed.
   */
  seasonYear?: string;
  /** Cost center ID — analytic accounting */
  costCenterId?: string;
  /** Grant ID if this entry is imputed to a public/private grant */
  grantId?: string;
  /** % of the amount imputed to the grant (0–100) */
  grantImputationPct?: number;
  /** Document reference (invoice/receipt number) */
  reference?: string;
  notes?: string;
  createdBy?: string;
  /** Club that made the payment (for INCOME entries from clubs) */
  clubId?: string;
  /** Payment method — BANK, CASH, CARD, etc. */
  paymentMethod?: string;
  /** Payment type — ENROLLMENT, LICENSE, etc. */
  paymentType?: string;
  /** Budget line ID this entry is assigned to (for budget tracking) */
  budgetLineId?: string;
  /**
   * ID of the card-payment movement this entry was registered from
   * («Registrar como apunte contable»). Prevents registering it twice.
   */
  movementId?: string;
  /**
   * ID of the referee receipt this entry was registered from
   * («Registrar en contabilidad» in Receipts). Prevents registering it twice.
   */
  receiptId?: string;
}

// ─── Budget Line (Línea presupuestaria) ─────────────────────────────────────
/**
 * A single line in the annual budget (presupuesto).
 * Stored in `tenants/{tenantId}/accounting_budget/{id}`.
 *
 * Linked to a season ("YYYY-YYYY" format) and tracks the estimated amount
 * per line. Actual amounts are computed from AccountingEntry records assigned
 * to the line via `budgetLineId`.
 */
export interface BudgetLine extends Entity {
  /** Season in "YYYY-YYYY" format, e.g. "2025-2026" */
  seasonId: string;
  type: AccountingEntryType;
  /** Budget estimate in euros */
  estimatedAmount: number;
  /** User-defined label for this line */
  name: string;
  /** Display order */
  order?: number;
  /** Parent BudgetLine ID — when set, this is a sub-line of another */
  parentId?: string;
}

// ─── Transparency export ─────────────────────────────────────────────────────

export interface TransparencyReport {
  fiscalYear: string;
  generatedAt: Date;
  totalIncome: number;
  totalExpenditure: number;
  balance: number;
  /** Income imputed to grants (subvenciones) */
  grantFunding: number;
  /** grantFunding as % of total income */
  grantFundingPct: number;
  costCentersBreakdown: {
    costCenter: string;
    income: number;
    expenditure: number;
  }[];
  /** Income/expenditure grouped by budget line (línea presupuestaria) */
  budgetLinesBreakdown: {
    label: string;
    amount: number;
    type: AccountingEntryType;
  }[];
}
